import React, { useContext, useEffect, useRef, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { StringObj } from "../../../type";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DoneIcon from "@mui/icons-material/Done";
import { get } from "lodash";
import { QuestionsContext, ReviewContext } from "../../../provider/MyContexts";
import { questionKeyLabels } from "../../../config";
import { getQuestionIds } from "../../../commands/questions/excelInfo";

const columnsLabels: {
  [key: string]: StringObj;
} = {
  excel: {
    reviewPoint: "観点",
    target: "該当箇所",
    problem: "問題点",
    // proposedRevision: "修正案",
    must: "",
    // commands: "",
  },

  llm: {
    questionId: "設問ID",
    reviewPoint: "観点",
    target: "該当箇所",
    problem: "問題点",
    // proposedRevision: "修正案",
    must: "",
    // questionLabel: "設問タイトル",
    // reviewTitle: "指摘事項と観点",
    // reviewComment: "コメント",
    // viewpoints: "観点",
    // commands: "",
  },
};

type Sort =
  | {
      key: string;
      order: "asc" | "desc";
    }
  | undefined;

export default function ResultsTable(props: { isFold?: boolean; isExtraLoading: boolean }) {
  const { reviews, reviewState, selectedId, handleSelectId } = useContext(ReviewContext);

  const { editSheet } = useContext(QuestionsContext);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  const [hideRows, setHideRows] = useState<string[]>([]);
  function handleHideRow(id: string) {
    if (hideRows.includes(id)) {
      setHideRows(hideRows.filter((rowId) => rowId !== id));
    } else {
      setHideRows([...hideRows, id]);
    }
  }

  const [doneRows, setDoneRows] = useState<string[]>([]);
  function handleDoneRow(id: string) {
    if (doneRows.includes(id)) {
      setDoneRows(doneRows.filter((rowId) => rowId !== id));
    } else {
      setDoneRows([...doneRows, id]);
    }
  }

  const columnsLabelsTarget = columnsLabels[reviewState || ""];

  const [sort, setSort] = useState<Sort>(undefined);
  const [sortedReviews, setSortedReviews] = useState<any[]>([]);

  function handleSort(key: string) {
    if (sort?.key === key) {
      if (sort.order === "desc") {
        setSort(undefined);
      } else if (sort.order === "asc") {
        setSort({
          key: key,
          order: "desc",
        });
      }
    } else {
      setSort({
        key: key,
        order: "asc",
      });
    }
  }

  useEffect(() => {
    if (!sort) {
      setSortedReviews(get(reviews, reviewState, []));
      return;
    }

    if (sort.key === "questionId") {
      getQuestionIds([], editSheet).then(({ excel }) => {
        let sorted: any[] = [];

        const reviewsTarget = [...get(reviews, reviewState, [])];

        sorted.push(
          ...reviewsTarget
            .filter((item) => !excel.includes(item.questionId))
            .sort((a: any, b: any) => {
              if (sort.order === "asc") {
                return a[sort.key] > b[sort.key] ? 1 : -1;
              } else {
                return a[sort.key] < b[sort.key] ? 1 : -1;
              }
            })
        );

        //
        (sort.order == "asc" ? excel : excel.reverse()).forEach((qId: any) => {
          const reviewsForQuestion = reviewsTarget.filter((a: any) => a.questionId === qId);
          if (reviewsForQuestion.length > 0) {
            sorted = sorted.concat(reviewsForQuestion);
          }
        });
        setSortedReviews(sorted);
      });
    } else {
      const sorted = get(reviews, reviewState, []).sort((a: any, b: any) => {
        if (sort.order === "asc") {
          return a[sort.key] > b[sort.key] ? 1 : -1;
        } else {
          return a[sort.key] < b[sort.key] ? 1 : -1;
        }
      });
      setSortedReviews(sorted);
    }
  }, [sort, reviews, reviewState, selectedId]);

  useEffect(() => {
    if (selectedId && rowRefs.current.has(selectedId)) {
      const row = rowRefs.current.get(selectedId);
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "nearest" });
        // row.scrollIntoView({ behavior: "smooth", block: "start" });
        // const offset = 100; // 上部にスペースを持たせたい場合
        // window.scrollBy(0, -offset);
      }
    }
  }, [selectedId]);

  // if (sort) {
  //   if (sort.key === "questionId") {
  //     const {excel} = await getQuestionIds([], "")

  //   }
  //   else {
  //   sortedReviews = get(reviews, reviewState, []).sort((a: any, b: any) => {
  //     if (sort.order === "asc") {
  //       return a[sort.key] > b[sort.key] ? 1 : -1;
  //     } else {
  //       return a[sort.key] < b[sort.key] ? 1 : -1;
  //     }
  //   });

  //   }
  // } else {
  //   sortedReviews = get(reviews, reviewState, []);
  // }

  if (!columnsLabelsTarget) {
    return <></>;
  }

  if (sortedReviews.length === 0 && !props.isExtraLoading) {
    return (
      <Stack width={"100%"} alignItems={"center"} justifyContent={"center"}>
        <Typography variant="body_M" padding={2}>
          {`レビュー結果がありません。`}
        </Typography>
      </Stack>
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        flex: 1,
        // maxHeight: props.isFold ? "calc(100vh - 180px - 240px)" : "calc(100vh - 180px)",
        maxHeight: "calc(100vh - 180px)",
        overflowY: "auto",
      }}
    >
      <Table aria-label="simple table" stickyHeader size="small">
        <TableHead>
          <TableRow>
            {Object.keys(columnsLabelsTarget).map((key) => (
              <TableCell key={key} onClick={() => handleSort(key)}>
                <Typography variant="label_M">{columnsLabelsTarget[key]}</Typography>
                {sort?.key === key ? (sort.order === "asc" ? "↑" : "↓") : ""}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedReviews.map((row, index) => {
            const isHide = hideRows.includes(row.id);
            const isDone = doneRows.includes(row.id);

            const isDoneRow = row.done;
            const isDismissRow = row.dismiss;

            return (
              // <React.Fragment key={index}>
              <TableRow
                key={index}
                sx={{
                  // "&:last-child td, &:last-child th": { border: 0 },
                  backgroundColor: selectedId === row.id ? "#e0e0e0" : "inherit",
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  !isHide && handleSelectId(row.id);
                }}
                ref={(el) => {
                  if (el) {
                    rowRefs.current.set(row.id, el);
                  }
                }}
              >
                {Object.keys(columnsLabelsTarget).map((key, colIndex) => {
                  if (key === "commands") {
                    return (
                      <TableCell key={colIndex}>
                        <Stack direction={"row"} spacing={1}>
                          <Tooltip title={isHide ? "無視しない" : "無視する"} placement="top">
                            <IconButton
                              onClick={(event: any) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleHideRow(row.id);
                              }}
                            >
                              {isHide ? (
                                <VisibilityIcon fontSize="small" />
                              ) : (
                                <VisibilityOffIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={isDone ? "未完了にする" : "完了済み"} placement="top">
                            <IconButton
                              onClick={(event: any) => {
                                event.preventDefault();
                                event.stopPropagation();
                                !isHide && handleDoneRow(row.id);
                              }}
                            >
                              <DoneIcon
                                fontSize="small"
                                sx={{
                                  fill: isDone ? "blue" : "gray",
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    );
                  }

                  if (key === "must") {
                    return (
                      <TableCell
                        key={colIndex}
                        component={colIndex == 0 ? "th" : "div"}
                        scope="row"
                        sx={{
                          // color: isHide ? "gray" : "inherit",
                          width: "96px",
                        }}
                      >
                        <Typography
                          variant="body_S"
                          sx={{
                            color: isDismissRow ? "gray" : "",
                            textDecoration: isDoneRow ? "line-through" : "",
                          }}
                        >
                          {row[key] == true ? "必須" : "任意"}
                        </Typography>
                      </TableCell>
                    );
                  }

                  let text = "";

                  text =
                    key == "problem" && row[key].length > 16
                      ? row[key].slice(0, 16) + "..."
                      : row[key];

                  text = key == "reviewPoint" ? get(questionKeyLabels, text, text) : text;
                  text = key == "questionId" && text == "whole_question" ? "全体設計" : text;

                  return (
                    <TableCell key={colIndex} component={colIndex == 0 ? "th" : "div"} scope="row">
                      <Typography
                        variant="body_S"
                        sx={{
                          color: isDismissRow ? "gray" : "",
                          textDecoration: isDoneRow ? "line-through" : "",
                        }}
                      >
                        {text}
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
